import React, {useMemo} from 'react';

import CircularProgress from '@mui/material/CircularProgress';

import ContentTreeContext from './ContentTreeContext';
import ContentTreeNode from './ContentTreeNode';
import ChildrenList from './ChildrenList';

const ContentTree = ({tree, renderNodePrefix, renderNodeSuffix, getNodeClassName, getNodeTooltip, getNodeIcon}) => {
    const {
        projects,
        expandedNodes,
        isLoading,
        loadingNodes,
        hasMoreProjects,
        handleExpand,
        handleLoadMoreProjects,
    } = tree;

    const ctx = useMemo(() => ({
        ...tree,
        renderNodePrefix,
        renderNodeSuffix,
        getNodeClassName,
        getNodeTooltip,
        getNodeIcon,
    }), [tree, renderNodePrefix, renderNodeSuffix, getNodeClassName, getNodeTooltip, getNodeIcon]);

    if (isLoading && projects.length === 0) {
        return (
            <div className="flex items-center justify-center py-8">
                <CircularProgress size={24} />
            </div>
        );
    }

    return (
        <ContentTreeContext.Provider value={ctx}>
            <div className="flex flex-col">
                {projects.map((project) => {
                    const isExpanded = expandedNodes.has(project.id);
                    const isNodeLoading = loadingNodes.has(project.id);

                    return (
                        <React.Fragment key={project.id}>
                            <ContentTreeNode
                                node={project}
                                nodeType="project"
                                depth={0}
                                isExpanded={isExpanded}
                                isLoading={isNodeLoading}
                                renderPrefix={renderNodePrefix ? () => renderNodePrefix(project, 'project') : undefined}
                                renderSuffix={renderNodeSuffix ? () => renderNodeSuffix(project, 'project') : undefined}
                                onExpand={() => handleExpand(project.id, 'project', project.id)}
                            />
                            {isExpanded && (
                                <ChildrenList
                                    parentNestedRoute={project.id}
                                    nodeId={project.id}
                                    depth={0}
                                />
                            )}
                        </React.Fragment>
                    );
                })}
                {hasMoreProjects && (
                    <LoadMoreButton onClick={handleLoadMoreProjects} isLoading={isLoading} />
                )}
            </div>
        </ContentTreeContext.Provider>
    );
};

const LoadMoreButton = ({onClick, isLoading}) => (
    <div
        className="flex items-center justify-center py-2 cursor-pointer text-xs text-link-normal hover:underline"
        onClick={isLoading ? undefined : onClick}
    >
        {isLoading ? <CircularProgress size={14} /> : 'Load more...'}
    </div>
);

export default ContentTree;
